import React, { useState, useEffect } from "react";
import { Box, Typography, Button, TextField, Paper } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ImageIcon from "@mui/icons-material/Image";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useUpdateAlbum } from "../../hooks/useAlbums";
import type { AlbumResponse } from "../../api/generated";

interface GalleryListItemProps {
  album: AlbumResponse;
  onEdit: (album: AlbumResponse) => void;
  onDelete: (album: AlbumResponse) => void;
  onView: (album: AlbumResponse) => void;
}

export const GalleryListItem: React.FC<GalleryListItemProps> = ({
  album,
  onEdit,
  onDelete,
  onView,
}) => {
  const updateAlbum = useUpdateAlbum();
  const [title, setTitle] = useState(album.title || "");
  const [description, setDescription] = useState(album.description || "");
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);

  // Update local state when album changes
  useEffect(() => {
    setTitle(album.title || "");
    setDescription(album.description || "");
  }, [album]);

  // Check if there are unsaved changes
  const hasUnsavedChanges =
    title !== (album.title || "") || description !== (album.description || "");

  // Handle title update
  const handleTitleBlur = () => {
    setEditingTitle(false);
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setTitle(album.title || "");
      setEditingTitle(false);
    }
  };

  // Handle description update
  const handleDescriptionBlur = () => {
    setEditingDescription(false);
  };

  const handleDescriptionKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      setDescription(album.description || "");
      setEditingDescription(false);
    }
  };

  // Handle save all changes
  const handleSave = async () => {
    const updates: { title?: string | null; description?: string | null } = {};

    if (title !== (album.title || "")) {
      updates.title = title || null;
    }
    if (description !== (album.description || "")) {
      updates.description = description || null;
    }

    if (Object.keys(updates).length > 0) {
      await updateAlbum.mutateAsync({
        albumId: album.id,
        data: updates,
      });
      setEditingTitle(false);
      setEditingDescription(false);
    }
  };

  // Generate optimized cover image URL
  const coverImageUrl = album.cover_image_url
    ? album.cover_image_url.replace("/upload/", "/upload/w_150,h_150,c_fill/")
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, sm: 1.5 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 0,
        backgroundColor: "background.paper",
        transition: (theme) => `all ${theme.custom.transitions.fast}`,
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: { xs: 0.75, sm: 1 },
        }}
      >
        {/* Header with Cover Image */}
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.75, sm: 1 },
            alignItems: "flex-start",
          }}
        >
          {coverImageUrl ? (
            <Box
              component="img"
              src={coverImageUrl}
              alt={album.title}
              sx={{
                width: { xs: 80, sm: 120 },
                height: { xs: 80, sm: 120 },
                flexShrink: 0,
                borderRadius: 0,
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              sx={{
                width: { xs: 80, sm: 120 },
                height: { xs: 80, sm: 120 },
                borderRadius: 0,
                backgroundColor: "grey.100",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ImageIcon
                sx={{ color: "grey.400", fontSize: { xs: "2rem", sm: "3rem" } }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 0.5, sm: 0.75 },
                mb: { xs: 0.25, sm: 0.5 },
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  color: album.is_public ? "success.main" : "text.secondary",
                }}
              >
                {album.is_public ? "Public" : "Private"}
              </Typography>
              {album.is_featured && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    color: "primary.main",
                  }}
                >
                  Featured
                </Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                display: "block",
              }}
            >
              <ImageIcon
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  verticalAlign: "middle",
                  mr: 0.5,
                }}
              />
              {album.image_count} {album.image_count === 1 ? "image" : "images"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                display: "block",
              }}
            >
              <VisibilityIcon
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  verticalAlign: "middle",
                  mr: 0.5,
                }}
              />
              {album.view_count || 0}{" "}
              {(album.view_count || 0) === 1 ? "view" : "views"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.65rem", sm: "0.7rem" },
                display: "block",
              }}
            >
              <CalendarTodayIcon
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.7rem" },
                  verticalAlign: "middle",
                  mr: 0.5,
                }}
              />
              {new Date(album.updated_at).toLocaleDateString()}
            </Typography>
          </Box>
        </Box>

        {/* Title - Inline Editable */}
        {editingTitle ? (
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            onKeyDown={handleTitleKeyDown}
            placeholder="Gallery title"
            size="small"
            fullWidth
            autoFocus
            disabled={updateAlbum.isPending}
            variant="standard"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                py: { xs: 0.25, sm: 0.375 },
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              py: { xs: 0.25, sm: 0.375 },
              "&:hover": {
                opacity: 0.7,
              },
            }}
            onClick={() => setEditingTitle(true)}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: title ? "text.primary" : "text.secondary",
                fontStyle: title ? "normal" : "italic",
                flex: 1,
                fontWeight: 500,
              }}
            >
              {title || "add title"}
            </Typography>
            <EditIcon
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: "text.secondary",
              }}
            />
          </Box>
        )}

        {/* Description - Inline Editable */}
        {editingDescription ? (
          <TextField
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            onKeyDown={handleDescriptionKeyDown}
            placeholder="Description"
            size="small"
            fullWidth
            autoFocus
            disabled={updateAlbum.isPending}
            variant="standard"
            sx={{
              "& .MuiInputBase-input": {
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                py: { xs: 0.25, sm: 0.375 },
              },
              "& .MuiInput-underline:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:hover:before": {
                borderBottom: "none",
              },
              "& .MuiInput-underline:after": {
                borderBottom: "none",
              },
            }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              py: { xs: 0.25, sm: 0.375 },
              "&:hover": {
                opacity: 0.7,
              },
            }}
            onClick={() => setEditingDescription(true)}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: description ? "text.primary" : "text.secondary",
                fontStyle: description ? "normal" : "italic",
                flex: 1,
              }}
            >
              {description || "add description"}
            </Typography>
            <EditIcon
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: "text.secondary",
              }}
            />
          </Box>
        )}

        {/* Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: { xs: 0.5, sm: 0.75 },
            pt: { xs: 0.25, sm: 0.5 },
          }}
        >
          <Button
            variant="text"
            startIcon={<SaveIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            }}
            disabled={!hasUnsavedChanges || updateAlbum.isPending}
            sx={{
              textTransform: "none",
              color: "primary.main",
              px: { xs: 0.5, sm: 1 },
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.25, sm: 0.5 },
              },
            }}
          >
            Save
          </Button>
          <Button
            variant="text"
            startIcon={<VisibilityIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onView(album);
            }}
            sx={{
              textTransform: "none",
              color: "text.primary",
              px: { xs: 0.5, sm: 1 },
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.25, sm: 0.5 },
              },
            }}
          >
            View
          </Button>
          <Button
            variant="text"
            startIcon={<EditIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onEdit(album);
            }}
            sx={{
              textTransform: "none",
              color: "text.primary",
              px: { xs: 0.5, sm: 1 },
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.25, sm: 0.5 },
              },
            }}
          >
            Edit
          </Button>
          <Button
            variant="text"
            startIcon={<DeleteIcon />}
            disableRipple
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(album);
            }}
            sx={{
              textTransform: "none",
              color: "error.main",
              px: { xs: 0.5, sm: 1 },
              minWidth: "auto",
              fontSize: { xs: "0.65rem", sm: "0.75rem" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: 0.25, sm: 0.5 },
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
